import axios from 'axios';
import { getToken } from './BearerToken';
import {
  endSession,
  ensureFreshToken,
  isRefusal,
  renewSession,
  shouldRenewAhead,
} from './SessionRenewal';

/**
 * Attach the bearer token — renewing it first if it is about to expire.
 *
 * An ID token lives one day, and the gateway's Cognito authorizer rejects an
 * expired one before any of our code runs. So the renewal is driven by the
 * REQUEST rather than by a timer: there is no window in which a call goes out
 * carrying a token we already knew was stale, whatever the tab was doing.
 * See SessionRenewal for the rest of the reasoning.
 */
axios.interceptors.request.use(
  async (config) => {
    // `skipSessionRenewal` marks the renewal call itself, so it can never
    // recurse into another renewal.
    if (!config.skipSessionRenewal && !config.sessionRetried && shouldRenewAhead()) {
      try {
        await ensureFreshToken();
      } catch {
        // Offline or a server fault: go ahead with the token we have and let
        // the response decide. A renewal that was REFUSED has already ended
        // the session inside ensureFreshToken.
      }
    }

    const token = getToken();

    if (token) {
      // eslint-disable-next-line no-param-reassign
      config.headers.Authorization = `Bearer ${token}`;
      // eslint-disable-next-line no-param-reassign
      config.headers['Content-Type'] = 'application/json';
    }

    return config;
  },
  (error) => Promise.reject(error),
);

/**
 * A 401 renews once and retries — the backstop for a clock that disagrees
 * with Cognito's, or a token whose expiry we could not read.
 *
 * Only a second 401, or a renewal the server refuses, ends the session: that
 * is the server saying this login is over (signed out, or its refresh token
 * revoked). Offline and 5xx say nothing about it.
 */
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error?.config;

    if (error?.response?.status !== 401 || !config || config.skipSessionRenewal) throw error;
    if (config.sessionRetried || !getToken()) {
      if (config.sessionRetried) endSession();
      throw error;
    }

    config.sessionRetried = true;

    try {
      await renewSession();
    } catch (renewError) {
      if (!isRefusal(renewError)) throw renewError;
      endSession();
      throw error;
    }

    return axios(config);
  },
);

// Build full URL, avoiding double slashes
const buildUrl = (url) => {
  const baseUrl = process.env.REACT_APP_API_URL.replace(/\/$/, ''); // Remove trailing slash
  const path = url.startsWith('/') ? url : `/${url}`;
  return baseUrl + path;
};

export function get(url, data) {
  const params = {
    ...data,
  };

  return axios.get(buildUrl(url), { params });
}

export function post(url, data, config = {}, params = {}) {
  return axios.post(buildUrl(url), data, { ...config, params });
}

export function put(url, data, config = {}, params = {}) {
  return axios.put(buildUrl(url), data, { ...config, params });
}

/**
 * DELETE with the identifiers in the query string, which is what every
 * endpoint in this app reads them from.
 *
 * `data` is the opt-in escape hatch for an endpoint that reads a JSON BODY on
 * a delete instead (axios sends `config.data` as the request body). Pass it
 * ALONGSIDE `params`, not instead of them, when it is not certain which side
 * the endpoint reads — a query string the handler ignores costs nothing.
 */
export function del(url, params, data) {
  const config = { params };
  if (data !== undefined) config.data = data;
  return axios.delete(buildUrl(url), config);
}
